import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import moment from "moment-timezone";
import ReactEcharts from "echarts-for-react";

import GYAxios from "../../util/GYAxios";
import usePeakLabels from "../../util/peakLabels";
import { useSite } from "../../util/sites";
import { useSolarCalc } from "../../util/calculations";

import LoaderGraph from "../general/LoaderGraph";

import AnalyticChartDateOptions from './AnalyticChartDateOptions';

const { parseFormatNumber } = useSolarCalc();

function AnalyticProductionDataGraph({ toggleCharts, selectedChart, setSelectedChart, ...props }) {
	const currentSite = useSite();
	const selectedSite = currentSite.selectedSite;

	let minDateStr = selectedSite.value.msa || selectedSite.value.cod || moment().add(1, 'year').toISOString(); // limit to MSA / COD date
	const minDate = useMemo(() => moment(minDateStr).startOf('date'), [ minDateStr ]);

	const [ data, setData ] = useState([]);
	const prevData = useRef(null);
	const [ graphData, setGraphData ] = useState(null);
	const defaultDataFilter = useMemo(() => ({ interval: '1M', range: '6M', chart: selectedChart, moments: [moment().subtract(5, 'month').startOf('month'), moment().startOf('month')] }), [ selectedChart ]);
	const [dataFilter, setDataFilter] = useState(defaultDataFilter);
	const dataIntervalFilterOptions = useMemo(() => ([
		{ label: 'Monthly', value: '1M', timeSpan: 1000*60*60*24*30, visibleRangeOptions: ['3M', '6M', '12M', '1Y', 'PAST_1Y'] },
		{ label: 'Yearly',  value: '1Y', timeSpan: 1000*60*60*24*365, visibleRangeOptions: ['CALENDAR_ALL', 'CONTRACT_ALL'] },
	]), []);
	const dataRangeFilterOptions = useMemo(() => ([
		{ label: 'Last 3 months', value: '3M', moments: [moment().subtract(2, 'month').startOf('month'), moment().startOf('day')] },
		{ label: 'Last 6 months', value: '6M', moments: [moment().subtract(5, 'month').startOf('month'), moment().startOf('day')] },
		{ label: 'Last 12 months', value: '12M', moments: [moment().subtract(11, 'month').startOf('month'), moment().startOf('day')] },
		{ label: 'This year', value: '1Y', moments: [moment().startOf('year'), moment().startOf('day')] },
		{ label: 'Last year', value: 'PAST_1Y', moments: [moment().subtract(1, 'year').startOf('year'), moment().subtract(1, 'year').endOf('year').startOf('day')] },
		{ label: 'All calendar years', value: 'CALENDAR_ALL', moments: [moment(minDate), moment().startOf('day')] },
		{ label: 'All contract years', value: 'CONTRACT_ALL', moments: [moment(minDate), moment().startOf('day')] },
	]), [ minDate ]);
	const dateRangeOtherOptions = useMemo(() => ([
		{
			label: 'Chart',
			name: 'chart',
			options: [
				{ label: 'Production', value: toggleCharts.productionChart },
				{ label: 'All Data', value: toggleCharts.sensorChart },
			]
		}
	]), [ toggleCharts ]);

	const dateRangeControlledOtherVal = useMemo(() => ({ chart: selectedChart }), [ selectedChart ]);
	const showHelperText = useMemo(() => dataFilter?.range === 'CONTRACT_ALL', [ dataFilter ]);
	const helperText = useMemo(() => {
		return (
			<div>
				<div>Contract year start on {selectedSite.value.msa ? 'MSA' : 'COD'} date in the contract.</div>
				<div>The first contract year is {minDate.format('MMM D, YYYY')} - {moment(minDate).add(1, 'year').subtract(1, 'day').format('MMM D, YYYY')},</div>
				<div>the second contract year is {moment(minDate).add(1, 'year').format('MMM D, YYYY')} - {moment(minDate).add(2, 'year').subtract(1, 'day').format('MMM D, YYYY')},</div>
				<div>and so on.</div>
			</div>
		);
	}, [ minDate, selectedSite ]);

	const solarLabel = usePeakLabels(selectedSite.value.country);

	const handleFilterOptionsChange = useCallback(val => {
		setDataFilter(val);
	}, []);

	const handleOtherOptionsChange = useCallback(val => {
		if('chart' in val) {
			setSelectedChart(val.chart);
		}
	}, [ setSelectedChart ]);

	useEffect(() => {
		if (dataFilter.interval !== '1M' && dataFilter.interval !== '1Y') { return }
		if (!dataFilter.moments?.length || typeof (selectedSite?.value?.site_id) !== 'number') { return }

		const siteId = selectedSite.value.site_id;
		const clientId = selectedSite.value.client_id;
		
		let fromDate = moment(dataFilter.moments[0]).startOf('date'); // create new instance to avoid reference conflicts
		let toDate   = moment(dataFilter.moments[1]).startOf('date'); // create new instance to avoid reference conflicts

		// fromDate must not be before MSA/COD date
		if(fromDate < minDate) { fromDate = minDate }
		if(toDate < fromDate) { toDate = moment(fromDate) }

		let interval, orderBy, API;
		
		if(dataFilter.interval === '1M') {
			interval = 'monthly';
			orderBy = 'dateData';
			fromDate = fromDate.format('YYYY-MM-DD');
			toDate   = toDate.format('YYYY-MM-DD');
			API = `/${interval}/site/${siteId}/period/${fromDate}/${toDate}/order/${orderBy}/ASC`;
		}
		if(dataFilter.interval === '1Y' && (dataFilter.range === 'CALENDAR_ALL' || dataFilter.range.type === 'DATE_RANGE_PICKER')) {
			// this will show all data including dates before MSA/COD
			interval = 'yearly';
			orderBy = 'yearData';
			fromDate = fromDate.format('YYYY-MM-DD');
			toDate   = toDate.format('YYYY-MM-DD');
			API = `/${interval}/client/${clientId}/site/${siteId}/period/${fromDate}/${toDate}/order/${orderBy}/ASC`;
		}
		if(dataFilter.interval === '1Y' && dataFilter.range === 'CONTRACT_ALL') {
			// fromDate must not be before MSA/COD date
			if(fromDate.diff(minDate) < 0) { fromDate = minDate }
			interval = 'daily';
			orderBy = 'dateData';
			fromDate = fromDate.format('YYYY-MM-DD');
			toDate   = toDate.format('YYYY-MM-DD');
			API = `/${interval}/site/${siteId}/period/${fromDate}/${toDate}/order/${orderBy}/ASC`;
		}

		GYAxios.get(API).then((res) => {
			const data = !res.data.error ? res.data : [];
			setData(data);
		});
	}, [ selectedSite, dataFilter, minDate ]);

	useEffect(() => {
		if(!solarLabel.every(label => label)) { return }
		if(prevData.current === data) { return }
		prevData.current = data;

		const interval = dataFilter.interval;
		const range = dataFilter.range;

		let dataToPlot = {
			dates: [],
			gridTotal: [],
			prodTotal: [],
			solarOFF: [],
			solarON: [],
			solarTotal: [],
		};

		// only for contract years
		let buffer = {
			solarON: 0,
			solarOFF: 0,
			solarTotal: 0,
			gridTotal: 0,
			prodTotal: 0,
		}
		let dateCursor = dataFilter.moments[0];

		data.forEach((resRow, index) => {
			if(range !== 'CONTRACT_ALL') {
				// format date
				if(/M$/gi.test(interval)) { dataToPlot.dates.push(moment(resRow.dateData).format('MMM YYYY')) }
				else if(/Y$/gi.test(interval)) { dataToPlot.dates.push(resRow.yearData) }
				else { dataToPlot.dates.push(moment(resRow.dateData).format('MM/DD/YYYY')) }

				const prodTotal = parseFloat(resRow.grid_total) + parseFloat(resRow.solar_total);
				dataToPlot.gridTotal.push(parseFormatNumber(resRow.grid_total, 2));
				dataToPlot.solarTotal.push(parseFormatNumber(resRow.solar_total, 2));
				dataToPlot.solarOFF.push(parseFormatNumber(resRow.solar_OFF, 2));
				dataToPlot.solarON.push(parseFormatNumber(resRow.solar_ON, 2));
				dataToPlot.prodTotal.push(parseFormatNumber(prodTotal, 3));
			}
			if(range === 'CONTRACT_ALL') {
				const rowDate = moment(resRow.dateData);
				// put cumulative data in dataToPlot
				const accumulateDataToPlot = () => {
					buffer.prodTotal += parseFloat(resRow.grid_total) + parseFloat(resRow.solar_total);
					buffer.solarTotal += resRow.solar_total;
					buffer.solarOFF += resRow.solar_OFF;
					buffer.solarON += resRow.solar_ON;
					buffer.gridTotal += resRow.grid_total;
				};
				const pushDataToPlot = () => {
					dataToPlot.dates.push(`Year ${dataToPlot.dates.length + 1}`);
					dataToPlot.gridTotal.push(parseFormatNumber(buffer.gridTotal, 2));
					dataToPlot.solarTotal.push(parseFormatNumber(buffer.solarTotal, 2));
					dataToPlot.solarOFF.push(parseFormatNumber(buffer.solarON, 2));
					dataToPlot.solarON.push(parseFormatNumber(buffer.solarOFF, 2));
					dataToPlot.prodTotal.push(parseFormatNumber(buffer.prodTotal, 3));
					buffer = {
						solarON: 0,
						solarOFF: 0,
						solarTotal: 0,
						gridTotal: 0,
						prodTotal: 0,
					}
				}
				if(index >= data.length - 1) {
					accumulateDataToPlot();
					pushDataToPlot();
					return;
				}
				if(rowDate.valueOf() >= moment(dateCursor).add(1, 'year').valueOf()) {
					dateCursor = moment(rowDate);
					pushDataToPlot();
					accumulateDataToPlot();
					return;
				}
				accumulateDataToPlot();
			}
		});

		let graphData = {
			color: ["#ffc602", "#f28a10", "#86bc25", "#12a8e0", "#c23531"],
			tooltip: {
				trigger: "axis",
				axisPointer: {
					type: "shadow",
				},
				formatter: function (params) {
					var i = 0;
					var total = 0;
					var total_alt = 0;
					var total_grid = 0;
					var total_prod = 0;

					var solar_on_selected = false;
					var solar_off_selected = false;
					var solar_total_selected = false;
					var grid_total_selected = false;
					var prod_total_selected = false;

					var colorON = "rgb(136,185,23, 0)";
					var colorOFF = "rgb(136,185,23, 0)";
					var colorTotal = "rgb(136,185,23, 0)";
					var colorTotalGrid = "rgb(136,185,23, 0)";
					var colorTotalProd = "rgb(136,185,23, 0)";

					var colorSpan = (color) =>
						'<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:' +
						color +
						'"></span>';
					let rez = "</br>";

					params.forEach((item) => {
						var xx;

						switch (item.seriesName) {
							case solarLabel[0]:
								colorON = item.color;
								solar_on_selected = true;
								break;

							case solarLabel[1]:
								colorOFF = item.color;
								solar_off_selected = true;
								break;

							case "Total Grid":
								colorTotalGrid = item.color;
								grid_total_selected = true;
								break;
							case "Total Production":
								colorTotalProd = item.color;
								prod_total_selected = true;
								break;

							default:
								colorTotal = item.color;
								solar_total_selected = true;
								break;
						}

						if (
							item.seriesName !== "Total Solar" &&
							item.seriesName !== "Total Grid" &&
							item.seriesName !== "Total Production"
						) {
							total += +parseFloat(item.data).toFixed(3);
							xx =
								"<p>" +
								colorSpan(item.color) +
								" " +
								params[i].axisValue +
								" " +
								item.seriesName +
								": " +
								parseFloat(item.data).toFixed(3) +
								" kWh" +
								"</p>";
							rez += xx;
						} else {
							if (item.seriesName === "Total Grid") {
								total_grid = parseFloat(item.data).toFixed(3);
							} else {
								if (item.seriesName === "Total Production") {
									total_prod = parseFloat(item.data).toFixed(3);
								} else total_alt += +parseFloat(item.data).toFixed(3);
							}
						}
						i++;
					});

					switch (true) {
						case solar_total_selected:
							rez += '<hr style="padding:0; margin:10px 0"/>';
							if (solar_on_selected && solar_off_selected)
								rez +=
									"<p>" +
									colorSpan(colorTotal) +
									"Total solar production: <b>" +
									total.toFixed(3) +
									"</b> kWh</p>";
							else
								rez +=
									"<p>" +
									colorSpan(colorTotal) +
									"Total solar production: <b>" +
									total_alt.toFixed(3) +
									"</b> kWh</p>";
							if (grid_total_selected) {
								rez +=
									"<p>" +
									colorSpan(colorTotalGrid) +
									"Total grid production: <b>" +
									total_grid +
									"</b> kWh</p>";
							}
							if (prod_total_selected) {
								rez +=
									"<p>" +
									colorSpan(colorTotalGrid) +
									"Total production: <b>" +
									total_prod +
									"</b> kWh</p>";
							}
							break;

						default:
							if (solar_on_selected && solar_off_selected) {
								rez += '<hr style="padding:0; margin:10px 0"/>';
								rez +=
									"<p>" +
									colorSpan(colorTotal) +
									"Total solar production: <b>" +
									total.toFixed(3) +
									"</b> kWh</p>";
							} else rez += "<br/>";

							if (grid_total_selected) {
								rez +=
									"<p>" +
									colorSpan(colorTotalGrid) +
									"Total grid production: <b>" +
									total_grid +
									"</b> kWh</p>";
							}
							if (prod_total_selected) {
								rez +=
									"<p>" +
									colorSpan(colorTotalProd) +
									"Total production: <b>" +
									total_prod +
									"</b> kWh</p>";
							}
							break;
					}
					return rez;
				},
			},
			title: {
				text: null,
				left: "center",
			},
			grid: {
				top: "20%",
			},
			legend: {
				data: [
					solarLabel[0],
					solarLabel[1],
					"Total Solar",
					"Total Grid",
					"Total Consumption",
				],
				selected: {
					"Total Solar": false,
					"Total Grid": false,
					"Total Consumption": false,
				},
				top: "7%",
			},
			toolbox: {
				show: true,
				left: "right",
				feature: {
					saveAsImage: {
						show: true,
						title: " ",
					},
				},
			},
			xAxis: {
				type: "value",
				axisLabel: {
					formatter: "{value} kWh",
				},
			},
			yAxis: {
				type: "category",
				name: "Dates",
				data: dataToPlot.dates,
				axisLine: { show: true },
				axisTick: {
					show: true,
				},
			},
			series: [
				{
					name: solarLabel[0],
					type: "bar",
					stack: "solar_stack",
					barGap: 0,
					label: {
						normal: {
							show: true,
							position: "insideRight",
							fontWeight: "bold",
							color: "white",
						},
					},
					data: dataToPlot.solarON,
				},
				{
					name: solarLabel[1],
					type: "bar",
					stack: "solar_stack",
					barGap: 0,
					label: {
						normal: {
							show: true,
							position: "insideRight",
							fontWeight: "bold",
							color: "white",
						},
					},
					data: dataToPlot.solarOFF,
				},
				{
					name: "Total Solar",
					type: "bar",
					barGap: 0,
					label: {
						normal: {
							show: true,
							position: "insideRight",
							fontWeight: "bold",
							color: "white",
						},
					},
					data: dataToPlot.solarTotal,
				},
				{
					name: "Total Grid",
					type: "bar",
					barGap: 0,
					label: {
						normal: {
							show: true,
							position: "insideRight",
							fontWeight: "bold",
							color: "white",
						},
					},
					data: dataToPlot.gridTotal,
				},
				{
					name: "Total Consumption",
					type: "bar",
					barGap: 0,
					label: {
						normal: {
							show: true,
							position: "insideRight",
							fontWeight: "bold",
						},
					},
					data: dataToPlot.prodTotal,
				},
			],
		};
		setGraphData(graphData);
	}, [ data, solarLabel, dataFilter ]);

	return (
		<div {...props} style={{ ...props.style, display: selectedChart !== toggleCharts.productionChart ? 'none' : 'block' }}>
			<h3 style={{ marginBottom: '0.3em' }}>Production Data</h3>
			<AnalyticChartDateOptions showRangeOption={true} defaultSelected={defaultDataFilter} intervalOptions={dataIntervalFilterOptions} otherOptions={dateRangeOtherOptions} rangeOptions={dataRangeFilterOptions} controlledOtherOptionVal={dateRangeControlledOtherVal} onChange={handleFilterOptionsChange} onOtherOptionsChange={handleOtherOptionsChange} minDate={minDate} showHelperText={showHelperText} helperText={helperText} />
			{graphData ? (
				<ReactEcharts
					style={{ height: "550px", padding: "0px 0px 20px 0px" }}
					option={graphData}
					notMerge={true}
					lazyUpdate={true}
				/>
			) : (
				<LoaderGraph style={{ height: "550px", padding: "20px 0px" }} />
			)}
		</div>
	);
}

export default AnalyticProductionDataGraph;
